/**
 * Users block on the Binom Advanced sub-tab. Mirrors the panel's
 * "Available for" table — each user gets a Read or Read & Write checkbox.
 * Toggling fires PUT /binom/campaigns/:id/permissions.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';

import { Checkbox } from '@/components/ui/form/controls/Checkbox';
import {
  useBinomPermissions,
  useUpdateBinomPermissions,
  type BinomCampaignPermissionUser,
} from '@/hooks/binom';

export interface BinomUsersSectionProps {
  campaignId: string | undefined;
}

const BinomUsersSection: FC<BinomUsersSectionProps> = ({ campaignId }) => {
  const q = useBinomPermissions(campaignId);
  const update = useUpdateBinomPermissions(campaignId);
  const data = q.data ?? { read: [], modify: [] };

  const allUsers = mergeUsers(data.read, data.modify);
  const inModify = new Set(data.modify.map((u) => u.id));

  const toggle = (user: BinomCampaignPermissionUser, write: boolean) => {
    if (!campaignId) return;
    const next = {
      read: data.read.filter((u) => u.id !== user.id || !write),
      modify: write
        ? [...data.modify.filter((u) => u.id !== user.id), user]
        : data.modify.filter((u) => u.id !== user.id),
    };
    if (write && !next.read.find((u) => u.id === user.id)) next.read.push(user);
    update.mutate(next);
  };

  return (
    <section className="space-y-2">
      <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        Available for
      </h4>
      {allUsers.length === 0 ? (
        <p className="text-[11px] italic text-muted-foreground">
          No users assigned.
        </p>
      ) : (
        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-2 py-1 text-left">User</th>
              <th className="px-2 py-1 text-right">Read &amp; Write</th>
            </tr>
          </thead>
          <tbody>
            {allUsers.map((u) => (
              <tr key={u.id} className="border-t border-border/30">
                <td className="px-2 py-1">
                  {u.name}{' '}
                  <span className="text-muted-foreground">({u.role})</span>
                </td>
                <td className="px-2 py-1 text-right">
                  <Checkbox
                    checked={inModify.has(u.id)}
                    disabled={update.isPending}
                    onCheckedChange={(v) => toggle(u, Boolean(v))}
                    aria-label={`Read & write for ${u.name}`}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
};

function mergeUsers(
  read: BinomCampaignPermissionUser[],
  modify: BinomCampaignPermissionUser[],
): BinomCampaignPermissionUser[] {
  const byId = new Map<number, BinomCampaignPermissionUser>();
  for (const u of [...read, ...modify]) byId.set(u.id, u);
  return Array.from(byId.values());
}

export default BinomUsersSection;
